import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { merge } from 'rxjs';

import {
  UploadPurpose,
  UploadService,
} from '../../../../core/services/uploads/upload.service';
import { Competency } from '../../../../core/services/questions/competency.service';
import { MathTextComponent } from '../../../../shared/math/math-text.component';
import { RichFieldComponent } from '../../../../shared/math/rich-field/rich-field.component';

import {
  ChoiceAnswersComponent,
  ChoiceAnswersForm,
  ChoiceMode,
  ChoiceOptionGroup,
  ChoiceOptionValue,
} from './answer-editors/choice-answers.component';
import {
  TextAnswersComponent,
  TextAnswersForm,
  TextAnswersValue,
  TextMatchOptions,
} from './answer-editors/text-answers.component';
import {
  NumericAnswerComponent,
  NumericAnswerForm,
  NumericAnswerValue,
} from './answer-editors/numeric-answer.component';

/* ══════════════════════════════════════════════════════════════
   TYPES PUBLICS
   ══════════════════════════════════════════════════════════════ */

export type QuestionType =
  | 'single_choice'
  | 'multiple_choice'
  | 'true_false'
  | 'short_text'
  | 'numeric';

export type DifficultyLevel = 'easy' | 'medium' | 'hard';

/**
 * Valeur manipulée par l'éditeur, en entrée (`initialValue`) comme en sortie
 * (`save`). Volontairement découplée des DTO HTTP : c'est l'écran parent qui
 * fait la traduction vers `QuestionBankCreateDto` / `QuestionBank`.
 */
/**
 * Les quatre colonnes du modèle d'importation, conservées telles quelles.
 *
 * <p>La source structurée reste la compétence liée — seule agrégeable pour dire
 * si un élève maîtrise une capacité. Ces libellés en sont la trace littérale.</p>
 */
export interface QuestionReferentialValue {
  /** Niveaux visés — colonne « المستوى ». */
  level?: string | null;
  /** Capacité évaluée — colonne « المهارة ». */
  skill?: string | null;
  /** Acquis antérieurs supposés — colonne « المكتسبات السابقة ». */
  prerequisites?: string | null;
  /** Compétence ciblée — colonne « المستهدف ». */
  targeted?: string | null;
}

export interface QuestionEditorValue {
  id?: number;
  questionType: QuestionType;
  title: string;
  description: string;
  points: number;
  difficultyLevel: DifficultyLevel;
  timeLimitSeconds: number | null;
  explanation: string;
  hints: string;
  tags: string[];
  /**
   * Illustration de l'énoncé : la clé d'objet renvoyée au dépôt, jamais une
   * URL — celle-ci est présignée et expire.
   */
  descriptionImageKey?: string | null;
  /** URL d'aperçu, fournie par le serveur ou par le dépôt qui vient d'aboutir. */
  descriptionImageUrl?: string | null;
  /**
   * Compétences évaluées. C'est ce rattachement qui fait entrer la question
   * dans le diagnostic : sans lui, elle ne dit rien de ce que l'élève maîtrise.
   */
  competencyIds?: number[];
  /**
   * Descripteurs du référentiel diagnostique, tels qu'écrits dans le fichier
   * d'origine — niveau, capacité, acquis supposés, compétence ciblée.
   *
   * <p>Ils voyagent dans le même `jsonb` que les réglages de correction, et ce
   * `jsonb` est réécrit **en entier** à chaque enregistrement. L'éditeur ne les
   * lisait pas : rouvrir puis enregistrer une question importée les effaçait,
   * sans que rien ne le dise. Ils traversent donc le formulaire, même quand
   * aucun champ ne les affiche.</p>
   */
  referential?: QuestionReferentialValue;
  /** Renseigné pour `single_choice`, `multiple_choice` et `true_false`. */
  choices?: ChoiceOptionValue[];
  /** Renseigné pour `short_text`. */
  textAnswers?: TextAnswersValue;
  /** Renseigné pour `numeric`. */
  numericAnswer?: NumericAnswerValue;
}

/** Une erreur de validation prête à être traduite dans le gabarit. */
export interface EditorError {
  key: string;
  params?: Record<string, unknown>;
}

/** Option du sélecteur de type. */
interface QuestionTypeOption {
  type: QuestionType;
  labelKey: string;
  icon: string;
}

/** Réponse telle qu'affichée dans l'aperçu élève. */
interface PreviewChoice {
  label: string;
  /** Clé i18n si le libellé est généré (vrai / faux) ; chaîne vide sinon. */
  labelKey: string;
  letter: string;
  isCorrect: boolean;
}

/** Modèle de l'aperçu élève, recalculé à chaque frappe. */
interface QuestionPreview {
  type: QuestionType;
  title: string;
  description: string;
  points: number | null;
  difficultyLevel: DifficultyLevel;
  timeLimitSeconds: number | null;
  tags: string[];
  hints: string;
  explanation: string;
  choices: PreviewChoice[];
  acceptedAnswers: string[];
  matchOptions: TextMatchOptions | null;
  numericValue: number | null;
  numericMin: number | null;
  numericMax: number | null;
}

type QuestionEditorForm = FormGroup<{
  questionType: FormControl<QuestionType>;
  title: FormControl<string>;
  description: FormControl<string>;
  points: FormControl<number | null>;
  difficultyLevel: FormControl<DifficultyLevel>;
  timeLimitSeconds: FormControl<number | null>;
  explanation: FormControl<string>;
  hints: FormControl<string>;
  tags: FormArray<FormControl<string>>;
  answers: FormGroup;
}>;

/* ══════════════════════════════════════════════════════════════
   CONSTANTES
   ══════════════════════════════════════════════════════════════ */

const QUESTION_TYPES: QuestionTypeOption[] = [
  {
    type: 'single_choice',
    labelKey: 'QUESTION_EDITOR.TYPE_SINGLE_CHOICE',
    icon: 'fa-regular fa-circle-dot',
  },
  {
    type: 'multiple_choice',
    labelKey: 'QUESTION_EDITOR.TYPE_MULTIPLE_CHOICE',
    icon: 'fa-regular fa-square-check',
  },
  {
    type: 'true_false',
    labelKey: 'QUESTION_EDITOR.TYPE_TRUE_FALSE',
    icon: 'fa-solid fa-toggle-on',
  },
  {
    type: 'short_text',
    labelKey: 'QUESTION_EDITOR.TYPE_SHORT_TEXT',
    icon: 'fa-solid fa-keyboard',
  },
  {
    type: 'numeric',
    labelKey: 'QUESTION_EDITOR.TYPE_NUMERIC',
    icon: 'fa-solid fa-hashtag',
  },
];

const DIFFICULTIES: { value: DifficultyLevel; labelKey: string }[] = [
  { value: 'easy', labelKey: 'QUESTION_EDITOR.DIFFICULTY_EASY' },
  { value: 'medium', labelKey: 'QUESTION_EDITOR.DIFFICULTY_MEDIUM' },
  { value: 'hard', labelKey: 'QUESTION_EDITOR.DIFFICULTY_HARD' },
];

/** Nombre minimum de réponses exigé par type. */
const MIN_OPTIONS: Record<QuestionType, number> = {
  single_choice: 2,
  multiple_choice: 3,
  true_false: 2,
  short_text: 1,
  numeric: 1,
};

const MAX_OPTIONS = 10;
const MAX_ACCEPTED_ANSWERS = 15;

/** Contenus stockés pour les deux réponses générées d'une question vrai/faux. */
const TRUE_FALSE_CONTENTS = ['true', 'false'];

/* ══════════════════════════════════════════════════════════════
   VALIDATEURS
   ══════════════════════════════════════════════════════════════ */

/** Une chaîne composée uniquement d'espaces ne vaut pas une valeur. */
function notBlankValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return typeof value === 'string' && value.trim().length === 0
    ? { blank: true }
    : null;
}

/** Les points d'une question doivent être strictement positifs. */
function strictlyPositiveValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? null : { positive: true };
}

/** La limite de temps est facultative, mais jamais négative. */
function optionalPositiveIntegerValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? null : { positive: true };
}

/**
 * Règles des types à choix :
 * - au moins `minOptions` réponses ;
 * - aucun libellé vide, aucun doublon ;
 * - exactement une bonne réponse en mode exclusif, au moins une sinon.
 */
function choiceAnswersValidator(mode: ChoiceMode, minOptions: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const options = (control as ChoiceAnswersForm).getRawValue().options ?? [];
    const errors: ValidationErrors = {};

    if (options.length < minOptions) {
      errors['minOptions'] = { min: minOptions, actual: options.length };
    }

    const contents = options.map((option) => (option.content ?? '').trim());
    if (contents.some((content) => content.length === 0)) {
      errors['blankOption'] = true;
    }

    const normalized = contents
      .filter((content) => content.length > 0)
      .map((content) => content.toLocaleLowerCase());
    if (new Set(normalized).size !== normalized.length) {
      errors['duplicateOption'] = true;
    }

    const correctCount = options.filter((option) => option.isCorrect).length;
    if (mode === 'multiple') {
      if (correctCount < 1) {
        errors['atLeastOneCorrect'] = true;
      }
    } else if (correctCount !== 1) {
      errors['exactlyOneCorrect'] = { actual: correctCount };
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}

/** Règles de `short_text` : au moins une réponse acceptée, non vide, non dupliquée. */
function textAnswersValidator(minAnswers: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control as TextAnswersForm).getRawValue();
    const answers = (raw.answers ?? []).map((answer) => (answer ?? '').trim());
    const errors: ValidationErrors = {};

    if (answers.length < minAnswers) {
      errors['minAnswers'] = { min: minAnswers, actual: answers.length };
    }
    if (answers.some((answer) => answer.length === 0)) {
      errors['blankAnswer'] = true;
    }

    const normalized = answers
      .filter((answer) => answer.length > 0)
      .map((answer) =>
        raw.caseSensitive ? answer : answer.toLocaleLowerCase(),
      );
    if (new Set(normalized).size !== normalized.length) {
      errors['duplicateAnswer'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}

/** Règles de `numeric` : une valeur finie obligatoire, une tolérance ≥ 0. */
function numericAnswerValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const raw = (control as NumericAnswerForm).getRawValue();
  const errors: ValidationErrors = {};

  if (raw.value === null || !Number.isFinite(Number(raw.value))) {
    errors['valueRequired'] = true;
  }
  if (
    raw.tolerance === null ||
    !Number.isFinite(Number(raw.tolerance)) ||
    Number(raw.tolerance) < 0
  ) {
    errors['toleranceInvalid'] = true;
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/* ══════════════════════════════════════════════════════════════
   COMPOSANT
   ══════════════════════════════════════════════════════════════ */

/**
 * Éditeur de question polymorphe.
 *
 * Composant **présentationnel** : aucun appel HTTP, aucune manipulation du DOM,
 * aucune dépendance à Bootstrap JS. Il reçoit éventuellement une question via
 * `initialValue` et émet la valeur saisie via `save` — la persistance est du
 * ressort de l'écran parent.
 */
@Component({
  selector: 'app-question-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ChoiceAnswersComponent,
    TextAnswersComponent,
    NumericAnswerComponent,
    MathTextComponent,
    RichFieldComponent,
  ],
  templateUrl: './question-editor.component.html',
  styleUrl: './question-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionEditorComponent implements OnChanges {
  /** Question à éditer, ou `null` pour une création. */
  @Input() initialValue: QuestionEditorValue | null = null;

  /**
   * Vrai quand la question est composée pour un test diagnostique : les
   * descripteurs du référentiel sont alors demandés.
   *
   * <p>Ailleurs, le rattachement se fait par le sélecteur de compétences — on
   * choisit dans ce qui existe plutôt que de le décrire. Afficher les deux
   * partout donnerait deux façons de faire le même geste.</p>
   */
  @Input() diagnostic = false;

  /**
   * Compétences proposables, celles de la matière du thème choisi.
   *
   * Fournies par l'écran hôte, qui seul connaît la portée retenue : l'éditeur
   * ignore parcours et matière, il ne manipule qu'une question.
   */
  @Input() competencies: Competency[] = [];

  /** Émis avec la valeur saisie quand le formulaire est valide. */
  @Output() save = new EventEmitter<QuestionEditorValue>();

  /** Émis quand l'utilisateur renonce à la saisie. */
  @Output() cancel = new EventEmitter<void>();

  readonly questionTypes = QUESTION_TYPES;
  readonly difficulties = DIFFICULTIES;
  readonly maxOptions = MAX_OPTIONS;
  readonly maxAcceptedAnswers = MAX_ACCEPTED_ANSWERS;

  /**
   * L'éditeur ne persiste rien, mais il dépose l'illustration lui-même : le
   * fichier n'a de sens qu'au moment de la saisie, et faire remonter un `File`
   * aux trois écrans qui hébergent cet éditeur y dupliquerait la même logique.
   * Seule la clé obtenue circule ensuite dans `save`.
   */
  private readonly uploads = inject(UploadService);

  /** Clé de l'illustration retenue, `null` si aucune. */
  readonly imageKey = signal<string | null>(null);

  /** URL d'aperçu correspondante. */
  readonly imageUrl = signal<string | null>(null);

  readonly imageUploading = signal(false);
  readonly imageError = signal<string | null>(null);

  /** Compétences retenues pour cette question. */
  readonly selectedCompetencies = signal<number[]>([]);

  isCompetencySelected(id: number): boolean {
    return this.selectedCompetencies().includes(id);
  }

  toggleCompetency(id: number): void {
    const current = this.selectedCompetencies();
    this.selectedCompetencies.set(
      current.includes(id) ? current.filter((c) => c !== id) : [...current, id],
    );
  }

  readonly form: QuestionEditorForm = this.buildForm();

  /** Contrôle de saisie d'une nouvelle étiquette (hors valeur émise). */
  readonly tagDraft = new FormControl('', { nonNullable: true });

  /**
   * Descripteurs du référentiel diagnostique — les quatre colonnes du modèle
   * d'importation, saisies ici à la main.
   *
   * <p>Tenus hors du formulaire principal, comme la saisie d'étiquette : ils ne
   * participent ni à la validation d'une question ni à sa correction. Le serveur
   * en tire la compétence ciblée, ses niveaux et ses prérequis — c'est ce
   * rattachement, et non ces libellés, qui rend le rapport de diagnostic
   * calculable.</p>
   */
  readonly referentialForm = new FormGroup({
    level: new FormControl('', { nonNullable: true }),
    skill: new FormControl('', { nonNullable: true }),
    prerequisites: new FormControl('', { nonNullable: true }),
    targeted: new FormControl('', { nonNullable: true }),
  });

  /** Type courant, mémorisé pour savoir ce qui peut être reporté d'un type à l'autre. */
  private currentType: QuestionType = 'single_choice';

  /** Incrémenté à chaque mutation du formulaire : socle réactif des `computed`. */
  private readonly revision = signal(0);

  /** Passe à `true` à la première tentative d'enregistrement. */
  readonly submitted = signal(false);

  /** Affichage de la solution sous l'aperçu élève. */
  readonly solutionVisible = signal(false);

  constructor() {
    merge(this.form.valueChanges, this.form.statusChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.revision.update((value) => value + 1));

    this.form.controls.questionType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((type) => this.applyType(type));
  }

  /* ── Entrées ─────────────────────────────────────────────── */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue']) {
      this.hydrate(this.initialValue);
    }
  }

  /**
   * Affiche les descripteurs : demandés pour un test diagnostique, et montrés
   * ailleurs dès que la question en porte.
   *
   * <p>Une question importée en porte toujours. Les cacher parce qu'on n'est
   * pas en mode diagnostique les rendrait invisibles mais bien présents — le
   * professeur ne saurait pas ce qu'il enregistre.</p>
   */
  get showReferential(): boolean {
    if (this.diagnostic) return true;

    return Object.values(this.referentialForm.getRawValue()).some(
      (v) => v.trim().length > 0,
    );
  }

  /* ── État dérivé (signals) ───────────────────────────────── */

  readonly questionType = computed<QuestionType>(() => {
    this.revision();
    return this.form.controls.questionType.value;
  });

  /** Mode de saisie transmis à l'éditeur de réponses à choix. */
  readonly choiceMode = computed<ChoiceMode>(() => {
    switch (this.questionType()) {
      case 'multiple_choice':
        return 'multiple';
      case 'true_false':
        return 'true_false';
      default:
        return 'single';
    }
  });

  readonly minOptions = computed<number>(() => MIN_OPTIONS[this.questionType()]);

  readonly isValid = computed<boolean>(() => {
    this.revision();
    return this.form.valid;
  });

  /** Erreurs des champs communs, affichées après une tentative d'enregistrement. */
  readonly commonErrors = computed<EditorError[]>(() => {
    this.revision();
    const errors: EditorError[] = [];
    const controls = this.form.controls;

    if (controls.title.hasError('required') || controls.title.hasError('blank')) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_TITLE_REQUIRED' });
    }
    if (
      controls.points.hasError('required') ||
      controls.points.hasError('positive')
    ) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_POINTS_POSITIVE' });
    }
    if (controls.timeLimitSeconds.hasError('positive')) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_TIME_LIMIT_POSITIVE' });
    }
    return errors;
  });

  /** Erreurs du bloc de réponses, traduites selon le type courant. */
  readonly answerErrors = computed<EditorError[]>(() => {
    this.revision();
    const raw = this.form.controls.answers.errors;
    if (!raw) {
      return [];
    }

    const errors: EditorError[] = [];
    if (raw['minOptions']) {
      errors.push({
        key: 'QUESTION_EDITOR.ERROR_MIN_OPTIONS',
        params: { min: raw['minOptions'].min },
      });
    }
    if (raw['blankOption']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_BLANK_OPTION' });
    }
    if (raw['duplicateOption']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_DUPLICATE_OPTION' });
    }
    if (raw['exactlyOneCorrect']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_EXACTLY_ONE_CORRECT' });
    }
    if (raw['atLeastOneCorrect']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_AT_LEAST_ONE_CORRECT' });
    }
    if (raw['minAnswers']) {
      errors.push({
        key: 'QUESTION_EDITOR.ERROR_MIN_ACCEPTED_ANSWERS',
        params: { min: raw['minAnswers'].min },
      });
    }
    if (raw['blankAnswer']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_BLANK_ACCEPTED_ANSWER' });
    }
    if (raw['duplicateAnswer']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_DUPLICATE_ACCEPTED_ANSWER' });
    }
    if (raw['valueRequired']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_NUMERIC_VALUE_REQUIRED' });
    }
    if (raw['toleranceInvalid']) {
      errors.push({ key: 'QUESTION_EDITOR.ERROR_NUMERIC_TOLERANCE' });
    }
    return errors;
  });

  /** Aperçu élève, recalculé en direct. */
  readonly preview = computed<QuestionPreview>(() => {
    this.revision();
    const raw = this.form.getRawValue();
    const type = raw.questionType;

    const view: QuestionPreview = {
      type,
      title: raw.title.trim(),
      description: raw.description.trim(),
      points: raw.points === null ? null : Number(raw.points),
      difficultyLevel: raw.difficultyLevel,
      timeLimitSeconds:
        raw.timeLimitSeconds === null ? null : Number(raw.timeLimitSeconds),
      tags: raw.tags,
      hints: raw.hints.trim(),
      explanation: raw.explanation.trim(),
      choices: [],
      acceptedAnswers: [],
      matchOptions: null,
      numericValue: null,
      numericMin: null,
      numericMax: null,
    };

    if (this.isChoiceType(type)) {
      const options: ChoiceOptionValue[] =
        (this.form.controls.answers as ChoiceAnswersForm).getRawValue()
          .options ?? [];
      view.choices = options.map((option, index) => ({
        label: option.content,
        labelKey:
          type === 'true_false'
            ? index === 0
              ? 'QUESTION_EDITOR.TRUE_LABEL'
              : 'QUESTION_EDITOR.FALSE_LABEL'
            : '',
        letter: String.fromCharCode(65 + index),
        isCorrect: option.isCorrect,
      }));
    } else if (type === 'short_text') {
      const textRaw = (this.form.controls.answers as TextAnswersForm).getRawValue();
      view.acceptedAnswers = (textRaw.answers ?? [])
        .map((answer) => answer.trim())
        .filter((answer) => answer.length > 0);
      view.matchOptions = {
        caseSensitive: textRaw.caseSensitive,
        trimWhitespace: textRaw.trimWhitespace,
        normalizeArabic: textRaw.normalizeArabic,
      };
    } else {
      const numericRaw = (
        this.form.controls.answers as NumericAnswerForm
      ).getRawValue();
      if (numericRaw.value !== null && Number.isFinite(numericRaw.value)) {
        const tolerance = Math.abs(Number(numericRaw.tolerance ?? 0));
        view.numericValue = numericRaw.value;
        view.numericMin = numericRaw.value - tolerance;
        view.numericMax = numericRaw.value + tolerance;
      }
    }

    return view;
  });

  /* ── Accès typés aux sous-formulaires ────────────────────── */

  get choiceForm(): ChoiceAnswersForm {
    return this.form.controls.answers as ChoiceAnswersForm;
  }

  get textForm(): TextAnswersForm {
    return this.form.controls.answers as TextAnswersForm;
  }

  get numericForm(): NumericAnswerForm {
    return this.form.controls.answers as NumericAnswerForm;
  }

  get tags(): FormArray<FormControl<string>> {
    return this.form.controls.tags;
  }

  /* ── Actions : réponses à choix ──────────────────────────── */

  onAddOption(): void {
    const options = this.choiceForm.controls.options;
    if (options.length >= MAX_OPTIONS) {
      return;
    }
    options.push(this.buildOption({ content: '', isCorrect: false }, false));
  }

  onRemoveOption(index: number): void {
    const options = this.choiceForm.controls.options;
    if (options.length <= this.minOptions()) {
      return;
    }
    options.removeAt(index);
  }

  /** Sélection exclusive : une seule bonne réponse (single_choice, true_false). */
  onCorrectSelected(index: number): void {
    const options = this.choiceForm.controls.options;
    options.controls.forEach((option, position) =>
      option.controls.isCorrect.setValue(position === index, {
        emitEvent: false,
      }),
    );
    options.updateValueAndValidity();
  }

  /* ── Actions : réponses libres ───────────────────────────── */

  onAddAcceptedAnswer(): void {
    const answers = this.textForm.controls.answers;
    if (answers.length >= MAX_ACCEPTED_ANSWERS) {
      return;
    }
    answers.push(new FormControl('', { nonNullable: true }));
  }

  onRemoveAcceptedAnswer(index: number): void {
    const answers = this.textForm.controls.answers;
    if (answers.length <= MIN_OPTIONS['short_text']) {
      return;
    }
    answers.removeAt(index);
  }

  /* ── Actions : étiquettes ────────────────────────────────── */

  addTag(): void {
    const raw = this.tagDraft.value.trim();
    this.tagDraft.setValue('');
    if (raw.length === 0) {
      return;
    }
    const exists = this.tags.controls.some(
      (control) => control.value.toLocaleLowerCase() === raw.toLocaleLowerCase(),
    );
    if (exists) {
      return;
    }
    this.tags.push(new FormControl(raw, { nonNullable: true }));
  }

  removeTag(index: number): void {
    this.tags.removeAt(index);
  }

  /* ── Actions : type, solution, soumission ────────────────── */

  selectType(type: QuestionType): void {
    if (this.form.controls.questionType.value === type) {
      return;
    }
    this.form.controls.questionType.setValue(type);
  }

  toggleSolution(): void {
    this.solutionVisible.update((visible) => !visible);
  }

  submit(): void {
    this.submitted.set(true);
    this.form.markAllAsTouched();
    this.revision.update((value) => value + 1);

    if (this.form.invalid) {
      return;
    }
    this.save.emit(this.buildValue());
  }

  onCancel(): void {
    this.cancel.emit();
  }

  isChoiceType(type: QuestionType): boolean {
    return (
      type === 'single_choice' ||
      type === 'multiple_choice' ||
      type === 'true_false'
    );
  }

  /* ── Construction du formulaire ──────────────────────────── */

  private buildForm(): QuestionEditorForm {
    return new FormGroup({
      questionType: new FormControl<QuestionType>('single_choice', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      title: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, notBlankValidator],
      }),
      description: new FormControl('', { nonNullable: true }),
      points: new FormControl<number | null>(1, {
        validators: [Validators.required, strictlyPositiveValidator],
      }),
      difficultyLevel: new FormControl<DifficultyLevel>('medium', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      // Facultative : sans valeur, la question n'est pas chronométrée. Une
      // valeur par défaut chronométrait toute question simplement enregistrée.
      timeLimitSeconds: new FormControl<number | null>(null, {
        validators: [optionalPositiveIntegerValidator],
      }),
      explanation: new FormControl('', { nonNullable: true }),
      hints: new FormControl('', { nonNullable: true }),
      tags: new FormArray<FormControl<string>>([]),
      answers: this.buildAnswersGroup('single_choice', null),
    });
  }

  private buildOption(
    value: ChoiceOptionValue,
    locked: boolean,
  ): ChoiceOptionGroup {
    return new FormGroup({
      content: new FormControl(
        { value: value.content, disabled: locked },
        { nonNullable: true },
      ),
      isCorrect: new FormControl(value.isCorrect, { nonNullable: true }),
    });
  }

  /**
   * Construit le sous-formulaire de réponses correspondant au type demandé.
   * `seed` permet de reprendre les libellés déjà saisis ou ceux d'une question
   * existante.
   */
  private buildAnswersGroup(
    type: QuestionType,
    seed: Partial<QuestionEditorValue> | null,
  ): FormGroup {
    const minOptions = MIN_OPTIONS[type];

    if (type === 'true_false') {
      const correctIndex = (seed?.choices ?? []).findIndex(
        (choice) => choice.isCorrect,
      );
      const options = TRUE_FALSE_CONTENTS.map((content, index) =>
        this.buildOption(
          {
            content,
            isCorrect: index === (correctIndex >= 0 ? correctIndex : 0),
          },
          true,
        ),
      );
      return new FormGroup(
        { options: new FormArray<ChoiceOptionGroup>(options) },
        { validators: [choiceAnswersValidator('true_false', minOptions)] },
      );
    }

    if (type === 'single_choice' || type === 'multiple_choice') {
      const mode: ChoiceMode = type === 'single_choice' ? 'single' : 'multiple';
      const carried = (seed?.choices ?? []).map((choice) => ({
        content: choice.content,
        isCorrect: choice.isCorrect,
      }));

      while (carried.length < minOptions) {
        carried.push({ content: '', isCorrect: false });
      }

      // Choix unique : on ne garde que la première bonne réponse.
      if (mode === 'single') {
        const firstCorrect = carried.findIndex((choice) => choice.isCorrect);
        carried.forEach((choice, index) => {
          choice.isCorrect = index === (firstCorrect >= 0 ? firstCorrect : 0);
        });
      }

      return new FormGroup(
        {
          options: new FormArray<ChoiceOptionGroup>(
            carried.map((choice) => this.buildOption(choice, false)),
          ),
        },
        { validators: [choiceAnswersValidator(mode, minOptions)] },
      );
    }

    if (type === 'short_text') {
      const seeded = seed?.textAnswers;
      const answers = (seeded?.answers ?? []).filter(
        (answer) => answer.trim().length > 0,
      );
      if (answers.length === 0) {
        answers.push('');
      }
      return new FormGroup(
        {
          answers: new FormArray<FormControl<string>>(
            answers.map(
              (answer) => new FormControl(answer, { nonNullable: true }),
            ),
          ),
          caseSensitive: new FormControl(seeded?.caseSensitive ?? false, {
            nonNullable: true,
          }),
          trimWhitespace: new FormControl(seeded?.trimWhitespace ?? true, {
            nonNullable: true,
          }),
          normalizeArabic: new FormControl(seeded?.normalizeArabic ?? true, {
            nonNullable: true,
          }),
        },
        { validators: [textAnswersValidator(minOptions)] },
      );
    }

    // numeric
    return new FormGroup(
      {
        value: new FormControl<number | null>(
          seed?.numericAnswer?.value ?? null,
        ),
        tolerance: new FormControl<number | null>(
          seed?.numericAnswer?.tolerance ?? 0,
        ),
      },
      { validators: [numericAnswerValidator] },
    );
  }

  /** Recompose le bloc de réponses en conservant les champs communs. */
  private applyType(next: QuestionType): void {
    const previous = this.currentType;
    this.currentType = next;

    // Les libellés se reportent uniquement entre choix unique et choix multiple.
    const canCarry =
      previous !== 'true_false' &&
      next !== 'true_false' &&
      this.isChoiceType(previous) &&
      this.isChoiceType(next);

    const seed: Partial<QuestionEditorValue> | null = canCarry
      ? { choices: this.choiceForm.getRawValue().options }
      : null;

    this.form.setControl('answers', this.buildAnswersGroup(next, seed));
  }

  /* ── Illustration de l'énoncé ────────────────────────────── */

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.imageError.set(null);
    this.imageUploading.set(true);

    this.uploads.upload(file, UploadPurpose.QuestionImage).subscribe({
      next: (result) => {
        this.imageKey.set(result.key);
        this.imageUrl.set(result.previewUrl);
        this.imageUploading.set(false);
      },
      error: () => {
        this.imageUploading.set(false);
        this.imageError.set('QUESTION_EDITOR.IMAGE_ERROR');
      },
    });

    // Permet de re-sélectionner le même fichier après un retrait.
    input.value = '';
  }

  removeImage(): void {
    this.imageKey.set(null);
    this.imageUrl.set(null);
    this.imageError.set(null);
  }

  /* ── Hydratation depuis `initialValue` ───────────────────── */

  private hydrate(value: QuestionEditorValue | null): void {
    this.submitted.set(false);
    this.solutionVisible.set(false);
    this.tagDraft.setValue('');

    this.imageKey.set(value?.descriptionImageKey ?? null);
    this.imageUrl.set(value?.descriptionImageUrl ?? null);
    this.imageError.set(null);
    this.imageUploading.set(false);
    this.selectedCompetencies.set([...(value?.competencyIds ?? [])]);

    // Repris même quand aucun champ ne les affiche : ils doivent ressortir tels
    // qu'ils sont entrés, sans quoi le serveur réécrirait le jsonb sans eux.
    this.referentialForm.setValue(
      {
        level: value?.referential?.level ?? '',
        skill: value?.referential?.skill ?? '',
        prerequisites: value?.referential?.prerequisites ?? '',
        targeted: value?.referential?.targeted ?? '',
      },
      { emitEvent: false },
    );

    const type: QuestionType = value?.questionType ?? 'single_choice';
    this.currentType = type;

    this.form.patchValue(
      {
        questionType: type,
        title: value?.title ?? '',
        description: value?.description ?? '',
        points: value?.points ?? 1,
        difficultyLevel: value?.difficultyLevel ?? 'medium',
        timeLimitSeconds: value?.timeLimitSeconds ?? null,
        explanation: value?.explanation ?? '',
        hints: value?.hints ?? '',
      },
      { emitEvent: false },
    );

    this.tags.clear({ emitEvent: false });
    (value?.tags ?? []).forEach((tag) =>
      this.tags.push(new FormControl(tag, { nonNullable: true }), {
        emitEvent: false,
      }),
    );

    // `setControl` déclenche valueChanges : les `computed` se rafraîchissent.
    this.form.setControl('answers', this.buildAnswersGroup(type, value));
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  /* ── Sortie ──────────────────────────────────────────────── */

  private buildValue(): QuestionEditorValue {
    const raw = this.form.getRawValue();
    const type = raw.questionType;

    const value: QuestionEditorValue = {
      questionType: type,
      title: raw.title.trim(),
      description: raw.description.trim(),
      points: Number(raw.points),
      difficultyLevel: raw.difficultyLevel,
      timeLimitSeconds:
        raw.timeLimitSeconds === null ? null : Number(raw.timeLimitSeconds),
      explanation: raw.explanation.trim(),
      hints: raw.hints.trim(),
      tags: [...raw.tags],
      descriptionImageKey: this.imageKey(),
      descriptionImageUrl: this.imageUrl(),
      competencyIds: this.selectedCompetencies(),
    };

    const referential = this.referentialForm.getRawValue();
    const described = Object.values(referential).some((v) => v.trim().length > 0);

    // Rien de renseigné, rien à transmettre : un objet de quatre chaînes vides
    // écraserait des descripteurs que le serveur détient.
    if (described) {
      value.referential = {
        level: referential.level.trim() || null,
        skill: referential.skill.trim() || null,
        prerequisites: referential.prerequisites.trim() || null,
        targeted: referential.targeted.trim() || null,
      };
    }

    if (this.initialValue?.id !== undefined) {
      value.id = this.initialValue.id;
    }

    if (this.isChoiceType(type)) {
      const options: ChoiceOptionValue[] =
        this.choiceForm.getRawValue().options ?? [];
      value.choices = options.map((option) => ({
        content: option.content.trim(),
        isCorrect: option.isCorrect,
      }));
    } else if (type === 'short_text') {
      const textRaw = this.textForm.getRawValue();
      value.textAnswers = {
        answers: (textRaw.answers ?? [])
          .map((answer) => answer.trim())
          .filter((answer) => answer.length > 0),
        caseSensitive: textRaw.caseSensitive,
        trimWhitespace: textRaw.trimWhitespace,
        normalizeArabic: textRaw.normalizeArabic,
      };
    } else {
      const numericRaw = this.numericForm.getRawValue();
      value.numericAnswer = {
        value: numericRaw.value === null ? null : Number(numericRaw.value),
        tolerance: Math.abs(Number(numericRaw.tolerance ?? 0)),
      };
    }

    return value;
  }
}
